import { Component, ElementRef, forwardRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { TableColumn, TableRowSelectorDirective, LegacyTableModule } from 'remarketing-ui-components-lib';
import { Select, Store } from '@ngxs/store';
import {
  Configuration,
  ConfigurationService,
  ConfigurationState,
  emailFormatValidator,
  noSpecialCharsEmailValidator,
  Notification,
  NotificationRecipient,
  NotificationService
} from 'remarketing-angular-library';
import { Observable, of, Subject } from 'rxjs';
import { map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CheckUnsavedForms } from '../../interfaces/check-unsaved-forms';
import { NotificationsService } from '../../services/notifications/notifications.service';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { SaComponent, AnalyticsService } from 'smartauction-frontend-micro-library';
import { AlertsOthersAddEditFormComponent } from './forms/add-edit-form/alerts-others-add-edit-form.component';
import { SuccessMessageComponent } from '../success-message/success-message.component';
import { ErrorMessageComponent } from '../error-message/error-message.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-alerts-others',
    templateUrl: './alerts-others.component.html',
    styleUrls: ['./alerts-others.component.scss'],
    providers: [{ provide: CheckUnsavedForms, useExisting: forwardRef(() => AlertsOthersComponent) }],
    imports: [TranslocoDirective, ErrorMessageComponent, SuccessMessageComponent, LegacyTableModule, AlertsOthersAddEditFormComponent, AsyncPipe]
})
export class AlertsOthersComponent extends SaComponent implements OnInit, CheckUnsavedForms {
  @Select(ConfigurationState.configuration) private configuration$: Observable<Configuration>;
  @ViewChild('footerDrawer') footerDrawer: TableRowSelectorDirective;
  tenantName$: Observable<string>;
  private cancelEditing$: Subject<boolean>;
  private remove$ = new Subject<any>();
  private undo$ = new Subject();
  private previous$ = new Subject<Array<NotificationRecipient>>();
  removedUser;
  removedUserError = false;
  users$: Observable<Array<NotificationRecipient>>;

  columns: TableColumn[] = [
    { key: 'name', label: '' },
    { key: 'role', label: '' },
    { key: 'email', label: '' },
    { key: 'mobilePhone', label: '' },
    { key: 'options', label: '' }
  ];

  columnsNoOptions: TableColumn[] = [
    { key: 'name', label: 'Name' },
    { key: 'role', label: 'Role' },
    { key: 'email', label: 'Email' },
    { key: 'mobilePhone', label: 'Phone' }
  ];

  cardConfig = {
    options: {
      fullWidth: true,
      showLabel: false
    }
  };

  addForm: UntypedFormGroup;
  editForm: UntypedFormGroup;
  editDrawer: TableRowSelectorDirective;

  showAddFormCancelWarning = false;
  showEditFormCancelWarning = false;

  visibilityFlags: Array<any> = [];
  buyAlerts: Array<Notification> = [];
  buyAlerts$: Observable<Array<Notification>>;
  sellAlerts$: Observable<Array<Notification>>;

  showSuccess = false;
  showError = false;
  message: string;
  messageType: 'add' | 'edit' | null = null;
  recipient: string;

  @ViewChild('pageHeading') pageHeading: ElementRef;
  routerState: any;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private analyticsService: AnalyticsService,
    private notificationService: NotificationService,
    private notificationsService: NotificationsService,
    private store: Store,
    private readonly configurationService: ConfigurationService,
    private readonly translocoService: TranslocoService
  ) {
    super();
    const currentNaviation = this.router.currentNavigation();

    if (currentNaviation && currentNaviation.extras && currentNaviation.extras.state) {
      this.routerState = currentNaviation.extras.state;
    }
  }

  ngOnInit() {
    this.getTranslations();
    this.tenantName$ = this.configuration$.pipe(map((configuration: Configuration) => configuration.tenantName));
    this.resetAddForm();
    this.remove$
      .pipe(
        takeUntil(this.destroyed$),
        withLatestFrom(this.notificationsService.otherRecipients$),
        tap(([userToRemove, currentUsers]) => {
          this.resetUserRemoveAlerts();
          this.showSuccess = false;
          this.showError = false;
          this.notificationService.deleteRecipient(userToRemove.uuid).pipe(take(1)).subscribe(
            () => {
              this.removedUser = userToRemove;
              this.notificationsService.otherRecipients$.next(currentUsers.filter(u => u.uuid !== userToRemove.uuid));
              this.previous$.next(currentUsers);
              this.scrollToSuccessMessage();
            },
            () => {
              this.showError = true;
              this.resetUserRemoveAlerts();
            }
          );
        })
      )
      .subscribe();
    this.undo$
      .pipe(
        takeUntil(this.destroyed$),
        withLatestFrom(this.previous$),
        tap(([_, previous]) => {
          this.showSuccess = false;
          this.showError = false;
          this.notificationService.createRecipient(this.removedUser).pipe(take(1)).subscribe(
            () => {
              this.notificationsService.otherRecipients$.next(previous);
              this.previous$.next(previous);
              this.resetUserRemoveAlerts();
            },
            () => {
              this.showSuccess = false;
              this.showError = true;
            }
          );
        })
      )
      .subscribe();

    this.users$ = this.notificationsService.otherRecipients$.asObservable();

    this.notificationsService.visibilityFlags$.pipe(takeUntil(this.destroyed$)).subscribe(flags => {
      this.visibilityFlags = flags;
    });
    this.buyAlerts$ = this.notificationsService.buyAlerts$.asObservable();
    this.sellAlerts$ = this.notificationsService.sellAlerts$.asObservable();

    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData.page.category.site}:Profile Settings:Manage Alerts:Others`,
        section: 'Profile Settings',
        subsection: 'Manage Alerts',
        title: 'Others'
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.routerState && this.routerState.focus) {
      this.pageHeading.nativeElement.focus();
    }
  }

  reformatPhone(phone) {
    if (!phone) {
      return undefined;
    } else {
      phone = phone.replace(/-/g, '');
      if (phone.length > 0) {
        if (phone.length === 11) {
          phone = phone.slice(1, 4) + '-' + phone.slice(4, 7) + '-' + phone.slice(7);
          return phone;
        } else {
          phone = phone.slice(0, 3) + '-' + phone.slice(3, 6) + '-' + phone.slice(6);
          return phone;
        }
      }
    }
  }

  cancelAdding() {
    if (this.hasUnsavedAddForm()) {
      this.showAddFormCancelWarning = true;
    } else {
      this.confirmCancelAdd();
    }
  }

  continueAdding() {
    this.showAddFormCancelWarning = false;
    if (this.cancelEditing$) {
      this.cancelEditing$.next(false);
      this.cancelEditing$.complete();
    }
  }

  confirmCancelAdd() {
    this.showAddFormCancelWarning = false;
    this.resetAddForm();
    this.footerDrawer.close();
    if (this.cancelEditing$) {
      this.cancelEditing$.next(true);
      this.cancelEditing$.complete();
    }
  }

  resetAddForm() {
    this.addForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, emailFormatValidator(), noSpecialCharsEmailValidator()]],
      role: [''],
      phone: ['', [Validators.minLength(10), Validators.maxLength(12)]]
    });
  }

  hasUnsavedForms(): boolean {
    return this.hasUnsavedAddForm() || this.hasUnsavedEditForm();
  }

  hasUnsavedAddForm(): boolean {
    /*
      Always show the confirmation messages as per
      RPPF-665, RPPF-666, RPPF-667
     */
    return this.footerDrawer.isOpen; /* && (this.showAddFormCancelWarning || this.addForm.dirty); */
  }

  hasUnsavedEditForm(): boolean {
    return this.editDrawer && this.editDrawer.isOpen && this.editForm.dirty;
  }

  showNavigationAlert(): Observable<boolean> {
    if (!this.hasUnsavedEditForm() && !this.hasUnsavedAddForm()) {
      return of(true);
    }

    if (this.hasUnsavedAddForm()) {
      this.showAddFormCancelWarning = true;
    } else if (this.hasUnsavedEditForm()) {
      this.showEditFormCancelWarning = true;
    }
    this.cancelEditing$ = new Subject<boolean>();
    return this.cancelEditing$.asObservable();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    event.preventDefault();
    if (this.hasUnsavedForms()) {
      event.returnValue = true;
    }
  }

  removeUser(user: any) {
    this.remove$.next(user);
  }

  undoRemove() {
    this.undo$.next(true);
  }

  addUser() {
    this.showAddFormCancelWarning = false;
    if (this.editDrawer && this.editDrawer.isOpen){
      if (this.hasUnsavedEditForm()) {
        this.cancelEditing();
        if(this.footerDrawer && this.footerDrawer.isOpen){
          this.footerDrawer.close();
        }
      } else{
        this.editDrawer.close();
        this.teardownEditForm();
        this.footerDrawer.open();
        this.resetAddForm();
        this.resetUserRemoveAlerts();
      }
      return;
    }
    this.footerDrawer.open();
    this.resetAddForm();
    this.resetUserRemoveAlerts();
  }

  saveAddForm(recipient) {
    const recipients = this.notificationsService.otherRecipients$.getValue();
    recipients.push(recipient);
    this.recipient = recipient;
    this.messageType = 'add';
    this.setAddMessage(recipient);
    this.notificationsService.otherRecipients$.next(recipients);
    this.footerDrawer.close();
    this.showSuccess = true;
    this.scrollToSuccessMessage();
  }

  setAddMessage(recipient) {
    this.translocoService
      .selectTranslate('profileManagement.alertsOthers.textSuccessfullyAdded', {recipient : recipient?.name})
      .pipe(take(1))
      .subscribe(translate => {
        this.message = translate;
      });
  }

  manageUser(user, tableRow: TableRowSelectorDirective) {
    if (this.footerDrawer && this.footerDrawer.isOpen) {
      if (this.hasUnsavedAddForm() && (this.showAddFormCancelWarning || this.addForm.dirty)) {
        this.cancelAdding();
        if(this.editDrawer.isOpen){
          this.editDrawer.close();
        }
        this.teardownAddForm();
      } else{
        this.footerDrawer.close();
        this.teardownAddForm();
        this.setupEditForm(user);
        this.editDrawer = tableRow;
        this.editDrawer.open();
        this.resetUserRemoveAlerts();
      }
      return;
    }
    if (this.hasUnsavedEditForm()) {
      this.cancelEditing();
    } else {
      if (this.editDrawer && this.editDrawer === tableRow) {
        this.editDrawer.close();
        this.teardownEditForm();
      } else {
        if (this.editDrawer && this.editDrawer !== tableRow) {
          this.editDrawer.close();
          this.teardownEditForm();
        }
        this.setupEditForm(user);
        this.editDrawer = tableRow;
        this.editDrawer.open();
        this.resetUserRemoveAlerts();
      }
    }
  }

  setupEditForm(user) {
    this.editForm = this.fb.group({
      name: [user.name || '', Validators.required],
      email: [user.email || '', [Validators.required, emailFormatValidator(), noSpecialCharsEmailValidator()]],
      role: [user.role || ''],
      phone: [user.mobilePhone || '', [Validators.minLength(10), Validators.maxLength(12)]]
    });
  }

  teardownEditForm() {
    this.editForm = undefined;
    this.editDrawer = undefined;
    this.showEditFormCancelWarning = false;
  }

  teardownAddForm(){
    this.addForm = undefined;
    this.showAddFormCancelWarning = false;
  }

  cancelEditing() {
    if (this.hasUnsavedEditForm()) {
      this.showEditFormCancelWarning = true;
    } else {
      this.confirmCancelEdit();
    }
  }

  continueEditing() {
    this.showEditFormCancelWarning = false;
    if (this.cancelEditing$) {
      this.cancelEditing$.next(false);
      this.cancelEditing$.complete();
    }
  }

  confirmCancelEdit() {
    this.showEditFormCancelWarning = false;
    this.editDrawer.close();
    this.teardownEditForm();
    if (this.cancelEditing$) {
      this.cancelEditing$.next(true);
      this.cancelEditing$.complete();
    }
  }

  saveEditForm(recipient) {
    const recipients = this.notificationsService.otherRecipients$.getValue();
    const index = recipients.findIndex(r => r.uuid === recipient.uuid);
    recipient.phone = this.reformatPhone(recipient.phone);
    recipients.splice(index, 1, recipient);
    this.recipient = recipient;
    this.messageType = 'edit';
    this.setEditMessage(recipient);
    this.notificationsService.otherRecipients$.next(recipients);
    this.editDrawer.close();
    this.teardownEditForm();
    this.showSuccess = true;
    this.scrollToSuccessMessage();
  }

  private scrollToSuccessMessage() {
    if (this.pageHeading?.nativeElement) {
      this.pageHeading.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.pageHeading.nativeElement.focus();
    }
  }

  setEditMessage(recipient) {
    this.translocoService
      .selectTranslate('profileManagement.alertsOthers.textSuccessfullySetting', {recipient : recipient?.name})
      .pipe(take(1))
      .subscribe(translate => {
        this.message = translate;
      });
  }

  userById(index: number, user: any) {
    return user.uuid;
  }

  private resetUserRemoveAlerts() {
    this.removedUser = undefined;
    this.removedUserError = false;
  }

  showAlert() {
    if (this.hasUnsavedEditForm()) {
      this.cancelEditing();
    } else if (this.hasUnsavedAddForm()) {
      this.cancelAdding();
    }
  }

  getTranslations(){
    this.translocoService.selectTranslateObject('profileManagement.alertsOthers.tableHeader').pipe(takeUntil(this.destroyed$)).subscribe(translate => {
      this.columns[0].label = translate.labelName;
      this.columns[1].label = translate.labelRole;
      this.columns[2].label = translate.labelEmail;
      this.columns[3].label = translate.labelPhone;
      this.columns[4].label = translate.labelOption;
      if (this.messageType === 'add') {
        this.setAddMessage(this.recipient);
      } else if (this.messageType === 'edit') {
        this.setEditMessage(this.recipient);
      }
    });
  }
}
